import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { TaskPanel as GipsyuiTaskPanel, ReviewRequestPanel, BlurLayer } from 'gipsy-ui'
import { constants, utils } from 'gipsy-misc'

import {
  updateTask,
  createTaskRequest,
  createRequestReview,
  patchTaskRequest,
  toggleCompleteTaskRequest,
  deleteTaskRequest,
  getListsBasedOnLocation,
  approveReview,
  reopenReview
} from 'features/task/store/actions'
import { handleAPIError } from 'features/app/store/actions'
import { progressPending, progressSuccess, progressError } from 'features/progressBar/store/actions'
import { closePanel, setEditMode } from 'features/taskPanel/store/actions'
import { markTaskAsRead } from 'features/notifications/store/actions'
import { findById } from 'features/list/utils'
import GestureHandler from 'features/taskPanel/components/gestureHandler'
import { getCurrentPathName } from 'utils/location'

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      closePanel,
      updateTask,
      createTaskRequest,
      createRequestReview,
      patchTaskRequest,
      toggleCompleteTaskRequest,
      deleteTaskRequest,
      handleAPIError,
      progressPending,
      progressError,
      progressSuccess,
      setEditMode,
      markTaskAsRead,
      getListsBasedOnLocation,
      approveReview,
      reopenReview
    },
    dispatch
  )
})

const mapStateToProps = state => ({
  uniqueKey: state.taskPanel.uniqueKey,
  active: state.taskPanel.active,
  loading: state.taskPanel.loading,
  mode: state.taskPanel.mode,
  editMode: state.taskPanel.editMode,
  task: state.taskPanel.item,
  notifications: state.notifications.list
})

export class TaskPanel extends PureComponent {
  static propTypes = {
    onCreate: PropTypes.func,
    onChange: PropTypes.func,
    onDelete: PropTypes.func
  }

  patcher = new utils.TaskPatcher(constants.delayBeforePatch.default)

  fieldsToChange = ['comments', 'reactions']

  onChange = (task, { paramName, value, method = 'change' }, extraParams) => {
    const { editMode, actions } = this.props
    const {
      getListsBasedOnLocation,
      toggleCompleteTaskRequest,
      closePanel,
      patchTaskRequest,
      updateTask,
      setEditMode
    } = actions
    return new Promise((resolve, reject) => {
      if (!task || !task.id) {
        return resolve()
      }

      /* quickfix because ETA is not returned by GET task by id */
      const taskInList = findById(task.id)
      if (taskInList) {
        task.ETA = taskInList.ETA
      }

      updateTask(task)

      if (paramName === 'completed') {
        toggleCompleteTaskRequest(task.id, value)
        closePanel()
        return resolve()
      }

      if (paramName === 'reviewStatus') {
        getListsBasedOnLocation(task)
        return
      }

      const { params, body } = utils.task.computePatchRequest(
        task,
        { paramName, value, method },
        { editMode, ...extraParams }
      )

      if (paramName === 'recurrencyDetails' && method === 'add') {
        setEditMode({ editMode: 'recurring' })
      }
      if (paramName === 'recurrencyDetails' && method === 'remove') {
        setEditMode({ editMode: 'standard' })
      }

      this.patcher.put(paramName, method, () => {
        return patchTaskRequest(task.id, params, body)
          .then(args => {
            resolve(args)
            this.props.onChange && this.props.onChange(task, { paramName, value, method })
          })
          .catch(reject)
      })
    })
  }

  create = async task => {
    const result = await this.props.actions.createTaskRequest(task, this.props.history, {
      componentSource: 'taskPanel',
      pageSource: getCurrentPathName()
    })
    this.props.onCreate && this.props.onCreate(task)
    return result
  }

  createRequestReview = async reviewRequest => {
    await this.props.actions.createRequestReview(reviewRequest)
    return
  }

  delete = async id => {
    const result = await this.props.actions.deleteTaskRequest(id, { deleteMode: this.props.editMode })
    this.props.onDelete && this.props.onDelete(id)
    return result
  }

  onUnmount = taskId => {
    this.props.actions.markTaskAsRead(taskId)
  }

  onEditModeChange = editMode => {
    this.props.actions.setEditMode({ editMode })
  }

  render() {
    let notifications = []
    if (this.props.task && this.props.task.id) {
      notifications = utils.notifications.getNotificationsByTaskId(this.props.notifications, this.props.task.id)
    }

    const { mode } = this.props

    switch (mode) {
      case 'reviewRequest':
        return (
          <BlurLayer
            name="task-panel"
            wrapper={GestureHandler}
            active={this.props.active}
            onClose={this.props.actions.closePanel}>
            {this.props.active ? (
              <ReviewRequestPanel
                defaultReviewRequest={this.props.task}
                onUnmount={this.onUnmount}
                create={this.createRequestReview}
                handleAPIError={this.props.actions.handleAPIError}
                progressPending={this.props.actions.progressPending}
                progressSuccess={this.props.actions.progressSuccess}
                progressError={this.props.actions.progressError}
                closePanel={this.props.actions.closePanel}
              />
            ) : (
              ''
            )}
          </BlurLayer>
        )
      default:
        return (
          <BlurLayer
            name="task-panel"
            wrapper={GestureHandler}
            active={this.props.active}
            onClose={this.props.actions.closePanel}>
            {this.props.active ? (
              <GipsyuiTaskPanel
                uniqueKey={this.props.uniqueKey}
                mode={mode}
                editMode={this.props.editMode}
                onEditModeChange={this.onEditModeChange}
                defaultTask={this.props.task}
                loading={this.props.loading}
                fieldsToChange={this.fieldsToChange}
                onChange={this.onChange}
                create={this.create}
                delete={this.delete}
                notifications={notifications}
                closePanel={this.props.actions.closePanel}
                onUnmount={this.onUnmount}
                handleAPIError={this.props.actions.handleAPIError}
                progressPending={this.props.actions.progressPending}
                progressSuccess={this.props.actions.progressSuccess}
                progressError={this.props.actions.progressError}
                approveReview={this.props.actions.approveReview}
                reopenReview={this.props.actions.reopenReview}
              />
            ) : (
              ''
            )}
          </BlurLayer>
        )
    }
  }
}

export const ConnectedTaskPanel = connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
  {
    pure: true
  }
)(TaskPanel)

export default withRouter(ConnectedTaskPanel)



// WEBPACK FOOTER //
// ./src/features/taskPanel/index.js